import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Label, NumberField, Typography } from '@heroui/react';
import { cn, getNodeTestIdProp } from '@/utils';
import { formatGel } from '../../sellerMeta';
import { PAYMENT_CASH_FORMAT } from '../paymentMeta';
import type { CombinedLeg, PaymentFlowValue } from '../usePaymentFlow';
import type { CombinedLegKeys } from '../useCombinedLegKeys';
import { Numpad, type NumpadProps } from './Numpad';
import { PaymentEntryNotice } from './PaymentEntryNotice';

type CombinedSectionProps = PaymentFlowValue &
    Pick<NumpadProps, 'flashedKey'> &
    Pick<CombinedLegKeys, 'legInputRefs' | 'onLegsKeyDown'>;

/**
 * Combined-payment step (render-only): a cash leg and a card leg that
 * auto-balance to the due. The seller selects a field (tap, `Tab`, or `↑`/`↓`
 * — bos-seller#59 — it becomes the active leg, shown with the accent ring)
 * and fills it from the physical digit keys or the shared numpad (both legs
 * are read-only displays); the other leg is always the remainder. Confirm
 * stays blocked only if the active leg is typed over the due, or a typed key
 * didn't fit an amount (a reserved notice slot below the numpad says why
 * without moving the keys). Values come from
 * `usePaymentFlow`; the `↑`/`↓` handling and the legs' refs from
 * `useCombinedLegKeys`.
 */
export const CombinedSection: React.FC<CombinedSectionProps> = ({
    activeLeg,
    selectLeg,
    combinedCash,
    combinedCard,
    combinedOver,
    combinedRemaining,
    entryNoticeKey,
    onPadKey,
    onPadDelete,
    padDeleteDisabled,
    flashedKey,
    legInputRefs,
    onLegsKeyDown,
}) => {
    const { t } = useTranslation('pages');

    // The fields are pad-driven display targets: tapping (focusing) one selects
    // it, routing the numpad there. Read-only so react-aria never swaps in its
    // own in-progress edit text over the numpad-driven value.
    const field = (
        leg: CombinedLeg,
        labelKey: string,
        testId: string,
        value: number,
    ) => (
        <div className="flex items-center gap-3">
            <Label className="w-20 flex-shrink-0 text-sm text-muted">
                {t(labelKey)}
            </Label>
            <NumberField
                aria-label={t(labelKey)}
                className="flex-1"
                formatOptions={PAYMENT_CASH_FORMAT}
                isReadOnly
                minValue={0}
                onClick={() => selectLeg(leg)}
                onFocus={() => selectLeg(leg)}
                value={value}
                variant="secondary"
                {...getNodeTestIdProp(testId)}
            >
                {/* Highlight the active leg on the group itself so the accent
                    outline coincides with the field's own border/shape. */}
                <NumberField.Group
                    className={cn(
                        '!grid-cols-1',
                        activeLeg === leg && 'border-accent ring-1 ring-accent',
                    )}
                >
                    <NumberField.Input
                        className="w-full tabular-nums"
                        ref={legInputRefs[leg]}
                    />
                </NumberField.Group>
            </NumberField>
        </div>
    );

    return (
        <div className="flex flex-col gap-3 px-1">
            <div className="flex flex-col gap-3" onKeyDown={onLegsKeyDown}>
                {field(
                    'cash',
                    'seller.payment.cash',
                    'seller-pay-combined-cash',
                    combinedCash,
                )}
                {field(
                    'card',
                    'seller.payment.card',
                    'seller-pay-combined-card',
                    combinedCard,
                )}
            </div>

            <Typography
                className={cn(
                    'text-center',
                    combinedOver ? 'text-danger' : 'text-muted',
                )}
                {...getNodeTestIdProp('seller-pay-combined-remaining')}
                type="body-sm"
            >
                {combinedOver
                    ? t('seller.payment.over', {
                          amount: formatGel(Math.abs(combinedRemaining)),
                      })
                    : t('seller.payment.autoSplitHint')}
            </Typography>

            <Numpad
                deleteDisabled={padDeleteDisabled}
                flashedKey={flashedKey}
                onDelete={onPadDelete}
                onKey={onPadKey}
            />

            <PaymentEntryNotice messageKey={entryNoticeKey} method="combined" />
        </div>
    );
};
